import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, NO_ERRORS_SCHEMA, OnDestroy, Output, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { AutocompleteChip } from "@app/models/autocomplete-chip.model";
import { Subscription, take, timer } from "rxjs";
import { AutoComplete, AutoCompleteModule } from "primeng/autocomplete";
import { SuggestMatchResult } from "@geocode-models/suggest-match-result.model";
import { GeocodingRestService } from "@services/rest/geocoding-rest.service";

@Component({
  selector: 'app-suggest-control',
  templateUrl: './suggest-control.component.html',
  styleUrls: ['./suggest-control.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AutoCompleteModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})

export class SuggestControlComponent implements OnDestroy {
  constructor(
    private readonly geocodingService: GeocodingRestService,
  ) { }

  @Output() AddressSelectedEvent = new EventEmitter<SuggestMatchResult | null>();
  @ViewChild('autocomplete') autocomplete!: AutoComplete;
  SuggestForm: FormGroup = this.createSuggestForm();
  public SuggestLoading: boolean = false;

  private currentQuery: string = '';

  private lastSearchedAddress: string = '';
  private readonly suggestDelayMs: number = 200;
  private suggestSubscription?: Subscription;
  public minimumTextLength: number = 3;

  public SuggestMatchResults: AutocompleteChip[] = [];
  private selectedMagicKey: string = '';

  ngOnDestroy(): void {
    if (this.suggestSubscription) {
      this.suggestSubscription.unsubscribe();
    }
  }

  public onInputChanged(event: any): void {
    this.currentQuery = event?.query ?? event;
    const addressText = this.currentQuery;
    this.getSuggestionResults();

    if (this.selectedMagicKey && (addressText != this.lastSearchedAddress)) {
      this.clearSelection();
    }
  }

  private createSuggestForm(): FormGroup {
    return new FormGroup({
      address: new FormControl<string>(''),
    });
  }

  private getSuggestionResults(): void {
    if (this.suggestSubscription) {
      this.suggestSubscription.unsubscribe();
    }
    this.suggestSubscription = timer(this.suggestDelayMs)
      .subscribe(() => {
        this.delayedSuggestTimer();
      });
  }

  public delayedSuggestTimer(): void {
    if (this.selectedMagicKey !== '') {
      return;
    }
    const addressText = this.currentQuery;
    const addressTextLength = this.getAddressLength(addressText);
    const shouldSearch: boolean = addressTextLength >= this.minimumTextLength;

    if (shouldSearch) {
      this.getSuggestions(addressText);
    }
  }

  private getAddressLength(addressText: string): number {
    const originalTextLength: number = addressText.length;
    let addressTextLength = addressText.trim().length;
    for (let i = 0; i < originalTextLength; i++) {
      const character = addressText[i].trim();
      if ((!character || character == '') || (character >= '0' && character <= '9')) {
        addressTextLength--;
      }
    }
    return addressTextLength;
  }

  private getSuggestions(addressText: string): void {
    this.SuggestLoading = true;
    this.lastSearchedAddress = addressText;

    this.geocodingService.suggest(addressText)
      .pipe(take(1))
      .subscribe((matchResults) => {
        const updatedMatchResults: AutocompleteChip[] = [];
        for (let i = 0; i < matchResults.length; i++) {
          let isSelected: boolean = this.selectedMagicKey === matchResults[i].magicKey;
          updatedMatchResults.push({
            id: i,
            name: matchResults[i].addressMatch,
            value: matchResults[i].addressMatch,
            displayValue: matchResults[i].addressMatch,
            isSelected: isSelected,
            addressMatch: matchResults[i],
          });
        }
        this.SuggestMatchResults = updatedMatchResults;
        this.SuggestLoading = false;
        if (this.SuggestMatchResults.length > 0) {
          this.autocomplete.show();
        } else {
          this.autocomplete.hide();
        }
      });
  }

  public onAddressSelected(event: any): void {
    if (!event) {
      return;
    }

    if (!event?.value?.addressMatch) {
      this.AddressSelectedEvent.emit(null);
      return;
    }
    this.selectedMagicKey = event.value.addressMatch.magicKey;
    this.AddressSelectedEvent.emit(event.value.addressMatch);
  }

  public onInputCleared(): void {
    this.clearSelection();
  }

  private clearSelection(): void {
    this.SuggestMatchResults = [];
    this.SuggestLoading = false;
    this.selectedMagicKey = '';
    this.AddressSelectedEvent.emit(null);
  }
}
